import { createBrowserRouter, RouterProvider } from 'react-router';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';

import AuthLayout from '@/layouts/auth-layout';
import AppLayout from '@/layouts/app-layout';
import { ProtectedRoute } from '@/components/protected-route';

import LoginPage from '@/pages/auth/login';
import SignupPage from '@/pages/auth/signup';
import DashboardPage from '@/pages/dashboard';
import BotListPage from '@/pages/bots/bot-list';
import BotNewPage from '@/pages/bots/bot-new';
import BotDetailPage from '@/pages/bots/bot-detail';
import StrategyListPage from '@/pages/strategies/strategy-list';
import StrategyDetailPage from '@/pages/strategies/strategy-detail';
import StrategyUploadPage from '@/pages/strategies/strategy-upload';
import StrategyFormPage from '@/pages/strategies/strategy-form';
import StrategyComparePage from '@/pages/strategies/strategy-compare';
import AccountListPage from '@/pages/accounts/account-list';
import AccountNewPage from '@/pages/accounts/account-new';
import AccountPortfolioPage from '@/pages/accounts/account-portfolio';
import BotActivityLogPage from '@/pages/bots/bot-activity-log';
import AlertsPage from '@/pages/alerts';
import ChallengeListPage from '@/pages/challenges/challenge-list';
import ChallengeNewPage from '@/pages/challenges/challenge-new';
import ChallengeDetailPage from '@/pages/challenges/challenge-detail';
import ChallengeLeaderboardPage from '@/pages/challenges/challenge-leaderboard';
import VerifyEmailPage from '@/pages/auth/verify-email';
import ForgotPasswordPage from '@/pages/auth/forgot-password';
import ResetPasswordPage from '@/pages/auth/reset-password';
import CheckEmailPage from '@/pages/auth/check-email';
import OAuthCallbackPage from '@/pages/auth/oauth-callback';
import OAuthAuthorizePage from '@/pages/auth/oauth-authorize';
import DemoAuthPage from '@/pages/auth/demo-auth';
import ApiKeysPage from '@/pages/settings/api-keys';
import ConnectedAppsPage from '@/pages/settings/connected-apps';
import DeveloperPage from '@/pages/settings/developer';
import CopyProvidersPage from '@/pages/copy-trade/copy-providers';
import CopyProviderDetailPage from '@/pages/copy-trade/copy-provider-detail';
import CopyAdminPage from '@/pages/copy-trade/copy-admin';
import CopyProviderAdminDetailPage from '@/pages/copy-trade/copy-provider-admin-detail';
import NotFoundPage from '@/pages/not-found';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'http://localhost:5174';

function RootRedirect() {
  const { token, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (token) {
      navigate('/dashboard', { replace: true });
    } else {
      window.location.replace(SITE_URL);
    }
  }, [token, isLoading, navigate]);

  return null;
}

const router = createBrowserRouter([
  // Root redirects to site landing page (or dashboard if authenticated)
  { path: '/', element: <RootRedirect /> },

  // Public auth routes
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
    ],
  },

  // Public email/password pages (standalone, no AuthLayout)
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/check-email', element: <CheckEmailPage /> },
  { path: '/auth/callback', element: <OAuthCallbackPage /> },
  { path: '/oauth/authorize', element: <OAuthAuthorizePage /> },

  // Protected app routes
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/bots', element: <BotListPage /> },
          { path: '/bots/new', element: <BotNewPage /> },
          { path: '/bots/:id', element: <BotDetailPage /> },
          { path: '/bots/:id/log', element: <BotActivityLogPage /> },
          { path: '/strategies', element: <StrategyListPage /> },
          { path: '/strategies/new/upload', element: <StrategyUploadPage /> },
          { path: '/strategies/new', element: <StrategyFormPage /> },
          { path: '/strategies/:id', element: <StrategyDetailPage /> },
          { path: '/strategies/:id/edit', element: <StrategyFormPage /> },
          { path: '/strategies/:id/compare', element: <StrategyComparePage /> },
          { path: '/accounts', element: <AccountListPage /> },
          { path: '/accounts/new', element: <AccountNewPage /> },
          {
            path: '/accounts/:mt5AccountId/portfolio',
            element: <AccountPortfolioPage />,
          },
          { path: '/copy-trade', element: <CopyProvidersPage /> },
          { path: '/copy-trade/admin', element: <CopyAdminPage /> },
          { path: '/copy-trade/admin/provider/:id', element: <CopyProviderAdminDetailPage /> },
          { path: '/copy-trade/:id', element: <CopyProviderDetailPage /> },
          { path: '/alerts', element: <AlertsPage /> },
          { path: '/challenges', element: <ChallengeListPage /> },
          { path: '/challenges/new', element: <ChallengeNewPage /> },
          { path: '/challenges/leaderboard', element: <ChallengeLeaderboardPage /> },
          { path: '/challenges/:id', element: <ChallengeDetailPage /> },
          { path: '/settings/api-keys', element: <ApiKeysPage /> },
          { path: '/settings/connected-apps', element: <ConnectedAppsPage /> },
          { path: '/settings/developer', element: <DeveloperPage /> },
        ],
      },
    ],
  },

  // Demo auth variants
  { path: '/demo/auth-a', element: <DemoAuthPage variant="A" /> },
  { path: '/demo/auth-b', element: <DemoAuthPage variant="B" /> },
  { path: '/demo/auth-c', element: <DemoAuthPage variant="C" /> },
  { path: '/demo/auth-d', element: <DemoAuthPage variant="D" /> },

  // Catch-all
  { path: '*', element: <NotFoundPage /> },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
